import { useEffect, useRef, type RefObject } from "react";
import { MathUtils, PerspectiveCamera, Vector3 } from "three";
import { projectDirection, useTourStoreApi } from "@heroncendre/player";

/**
 * ============================================================================
 *  useProjectionHotspots.ts — Suivre des points du panorama avec du DOM
 * ----------------------------------------------------------------------------
 *  Le registre du moteur (`HotspotRegistry`) n'est pas exposé à la coquille : on
 *  ne peut pas y inscrire d'élément. On refait donc sa projection avec les mêmes
 *  fonctions exportées (`projectDirection`, `sphericalToDirection`), sur une
 *  caméra de travail posée comme celle du moteur (`CameraRig` : ordre YXZ, cap
 *  négatif, champ borné par le document).
 *
 *  Chaque image, `poser` reçoit la position écran du point (ou `null` s'il est hors
 *  champ) et écrit directement sur l'élément : jamais de rendu React à la cadence
 *  de l'écran. Sert aux noms des destinations (`NomsHotspots`) et à la pulsation
 *  des points (`PulseHotspots`).
 * ==========================================================================*/

export interface PointSuivi {
  id: string;
  direction: Vector3;
}

export interface CadreProjection {
  width: number;
  height: number;
  /** Champ de vision vertical effectif, en degrés. */
  fov: number;
}

export function useProjectionHotspots(
  cadre: RefObject<HTMLElement | null>,
  points: PointSuivi[],
  actif: boolean,
  poser: (id: string, ecran: { x: number; y: number } | null, cadre: CadreProjection) => void,
) {
  const store = useTourStoreApi();
  const poserRef = useRef(poser);
  poserRef.current = poser;

  useEffect(() => {
    if (!actif || points.length === 0) return;
    const camera = new PerspectiveCamera();
    camera.rotation.order = "YXZ";
    const scratch = new Vector3();
    let image = 0;
    const pas = () => {
      const zone = cadre.current;
      if (zone) {
        const { width, height } = zone.getBoundingClientRect();
        const { view, tour } = store.getState();
        const { min, max } = tour.settings.fovRange;
        const fov = Math.min(max, Math.max(min, view.fov ?? tour.settings.defaultFov));
        camera.fov = fov;
        camera.aspect = width / Math.max(1, height);
        camera.rotation.y = -MathUtils.degToRad(view.yaw);
        camera.rotation.x = MathUtils.degToRad(view.pitch);
        camera.rotation.z = 0;
        camera.updateProjectionMatrix();
        camera.updateMatrixWorld();
        for (const point of points) {
          const projection = projectDirection(point.direction, camera, width, height, 0.05, scratch);
          poserRef.current(point.id, projection.visible ? { x: projection.x, y: projection.y } : null, { width, height, fov });
        }
      }
      image = requestAnimationFrame(pas);
    };
    image = requestAnimationFrame(pas);
    return () => cancelAnimationFrame(image);
  }, [store, cadre, points, actif]);
}
